import React, { MouseEvent } from 'react';

import { Form, useFormikContext } from 'formik';
import { Trans, useTranslation } from 'react-i18next';

import FormikCheckboxField from '@components/FormElements/FormikFields/Checkbox';
import { H3, Paragraph } from '@components/Typography';
import { Button } from '@components/Buttons';
import { FieldsGroup } from '@components/FormElements/styled';
import { Group, LegalLink } from '@components/Auth/SignUp/styled';
import InputField from '@components/FormElements/FormikFields/Input';
import FormikInputMaskField from '@components/FormElements/FormikFields/InputMask';
import PasswordField from '@components/FormElements/FormikFields/Password';
import MultiRadio from '@components/FormElements/BaseFields/MultiRadio';
import { RegisterUser } from '@/types/user';
import { LEGAL_CONDITIONS, PRIVACY_POLICY } from '@utils/paths';

interface Props {
  handleSignInClick: (e: MouseEvent) => void;
  isLoading: boolean;
}

const SignUpForm = ({ handleSignInClick, isLoading }: Props) => {
  const { t } = useTranslation();

  const { setFieldValue, values, errors, touched } = useFormikContext<RegisterUser>();

  const radioFemale = {
    value: 'FEMALE',
    label: t('option.gender.FEMALE'),
  };
  const radioMale = {
    value: 'MALE',
    label: t('option.gender.MALE'),
  };
  const radioOther = {
    value: 'OTHER',
    label: t('option.gender.OTHER'),
  };

  return (
    <div style={{ width: '100%' }}>
      <Form>
        <H3>{t('forms.signUp.caption')}</H3>
        <>
          <FieldsGroup>
            <MultiRadio
              radioValues={[radioFemale, radioMale, radioOther]}
              name="gender"
              label={`${t('forms.signUp.label.gender')}  (*)`}
              error={touched && touched.gender && errors.gender ? errors.gender : undefined}
              setFieldValue={setFieldValue}
              selectedValue={values.gender}
              isLoading={isLoading}
            />
            <Group>
              <InputField
                name="firstName"
                fieldProps={{ label: `${t('forms.signUp.label.firstName')} (*)`, isLoading }}
                placeholder={t('forms.signUp.placeholder.firstName')}
              />
              <InputField
                name="lastName"
                fieldProps={{ label: `${t('forms.signUp.label.lastName')} (*)`, isLoading }}
                placeholder={t('forms.signUp.placeholder.lastName')}
              />
            </Group>
            <Group>
              <PasswordField
                name="password"
                fieldProps={{ label: `${t('forms.signUp.label.password')} (*)`, isLoading }}
                placeholder={t('forms.signUp.placeholder.password')}
              />
              <PasswordField
                name="confirmedPassword"
                fieldProps={{ label: `${t('forms.signUp.label.passwordRepeat')} (*)`, isLoading }}
                placeholder={t('forms.signUp.placeholder.passwordRepeat')}
              />
            </Group>
            <Group>
              <InputField
                name="email"
                fieldProps={{ label: `${t('forms.signUp.label.email')} (*)`, isLoading }}
                placeholder={t('forms.signUp.placeholder.email')}
              />
            </Group>
            <Group>
              <FormikInputMaskField
                name="birthDate"
                mask="99/99/9999"
                placeholder={t('forms.signUp.placeholder.birthDate')}
                fieldProps={{ label: `${t('forms.signUp.label.birthDate')} (*)`, isLoading }}
              />
            </Group>
            <Group>{!isLoading && <FormikCheckboxField label="" name="policies" checked={false} />}</Group>
            <Paragraph
              style={{
                maxWidth: '100%',
                marginTop: '-30px',
                marginLeft: '25px',
                fontSize: '12px',
                lineHeight: '14px',
                color: '#333333',
                display: 'block',
              }}
            >
              <Trans i18nKey="forms.signUp.cta.title">
                VAR0
                <LegalLink style={{ zIndex: 3, position: 'relative' }} to={LEGAL_CONDITIONS}>
                  VAR1
                </LegalLink>
                VAR4
                <LegalLink style={{ zIndex: 3, position: 'relative' }} to={PRIVACY_POLICY}>
                  VAR3
                </LegalLink>
                VAR5
              </Trans>
            </Paragraph>
            <Group>
              {!isLoading && (
                <FormikCheckboxField
                  label="Acepto recibir comunicaciones por email"
                  name="platformCommunications"
                  checked={false}
                />
              )}
            </Group>
          </FieldsGroup>
        </>
        <FieldsGroup>
          <Button isDisabled={isLoading} type="submit">
            {t('forms.signUp.cta.submit')}
          </Button>
          <Button $type="ghost" onClick={handleSignInClick}>
            {t('forms.signUp.cta.logIn')}
          </Button>
        </FieldsGroup>
      </Form>
    </div>
  );
};

export default SignUpForm;
